import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  approveSituationCard,
  beginSituationCardEdit,
  deleteSituationConcept,
  fetchSituationCard,
  fetchPopularSituationSources,
  fetchSituationCards,
  fetchSituationSources,
  fetchSituationVersion,
  fetchSituationVersions,
  generateSituationCard,
  omitSituationSources,
  publishSituationCard,
  reopenSituationCard,
  replaceCardRoles,
  replaceCardSources,
  replaceSlotSources,
  resetSituationCards,
  restoreSituationVersion,
  type SituationCardListParams,
  type SituationSourceParams,
} from "./situation-cards.js";
import type { SituationRole, SituationSlotKey } from "@peer-start/platform-contracts";

const root = ["situation-cards"] as const;

export function useSituationCards(params: SituationCardListParams) {
  return useQuery({ queryKey: [...root, "list", params], queryFn: () => fetchSituationCards(params) });
}

export function useSituationCard(cardId: string, enabled = true) {
  return useQuery({
    queryKey: [...root, "detail", cardId],
    queryFn: () => fetchSituationCard(cardId),
    enabled: enabled && cardId.length > 0,
  });
}

export function useSituationSources(params: SituationSourceParams, enabled: boolean) {
  return useQuery({
    queryKey: [...root, "sources", params],
    queryFn: () => fetchSituationSources(params),
    enabled,
  });
}

export function usePopularSituationSources(enabled: boolean) {
  return useQuery({
    queryKey: [...root, "sources", "popular"],
    queryFn: fetchPopularSituationSources,
    enabled,
  });
}

export function useSituationVersions(cardId: string) {
  return useQuery({ queryKey: [...root, "versions", cardId], queryFn: () => fetchSituationVersions(cardId) });
}

export function useSituationVersion(cardId: string, version: number | null) {
  return useQuery({
    queryKey: [...root, "version", cardId, version],
    queryFn: () => fetchSituationVersion(cardId, version!),
    enabled: version !== null,
  });
}

export function useResetSituationCards() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resetSituationCards,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: root });
    },
  });
}

export function useSituationCardMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      action:
        | { type: "card-sources"; cardId: string; sourceIds: string[] }
        | { type: "roles"; cardId: string; roles: SituationRole[] }
        | { type: "slot-sources"; cardId: string; slotKey: SituationSlotKey; sourceIds: string[] }
        | { type: "generate"; cardId: string }
        | { type: "omit-sources"; cardId: string; sourceIds: string[] }
        | { type: "publish"; cardId: string; sourceIds?: string[] }
        | { type: "approve"; cardId: string }
        | { type: "begin-edit"; cardId: string }
        | { type: "reopen"; cardId: string }
        | { type: "delete-concept"; cardId: string }
        | { type: "restore"; cardId: string; version: number },
    ) => {
      switch (action.type) {
        case "card-sources":
          return replaceCardSources(action.cardId, action.sourceIds);
        case "roles":
          return replaceCardRoles(action.cardId, action.roles);
        case "slot-sources":
          return replaceSlotSources(action.cardId, action.slotKey, action.sourceIds);
        case "generate":
          return generateSituationCard(action.cardId);
        case "omit-sources":
          return omitSituationSources(action.cardId, action.sourceIds);
        case "publish":
          return publishSituationCard(action.cardId, action.sourceIds ?? []);
        case "approve":
          return approveSituationCard(action.cardId);
        case "begin-edit":
          return beginSituationCardEdit(action.cardId);
        case "reopen":
          return reopenSituationCard(action.cardId);
        case "delete-concept":
          return deleteSituationConcept(action.cardId);
        case "restore":
          return restoreSituationVersion(action.cardId, action.version);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: root });
    },
  });
}
